/**
 * Opens a full-screen image preview overlay using plain DOM.
 * Completely outside React/Slate — no event bubbling issues.
 *
 * Features:
 *  - Scroll wheel / trackpad to zoom in/out
 *  - Click + drag to pan (when zoomed in)
 *  - Double-click to toggle zoom
 *  - +/- keys to zoom
 *  - Escape or click backdrop to close
 */
export function openImagePreview(src, alt) {
    let scale = 1;
    let translateX = 0;
    let translateY = 0;
    let isDragging = false;
    let dragStartX = 0;
    let dragStartY = 0;
    let lastTranslateX = 0;
    let lastTranslateY = 0;

    const MIN_SCALE = 0.25;
    const MAX_SCALE = 8;
    const ZOOM_STEP = 0.08;

    // --- Overlay ---
    const overlay = document.createElement('div');
    Object.assign(overlay.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100vw',
        height: '100vh',
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // Above the editor drawers (10100) and dialogs lifted over them (10201): opened from
        // comments in an editor's pane or from the Smart Check modal, 10000 put it behind them.
        zIndex: '10202',
        cursor: 'zoom-in',
        overflow: 'hidden',
        userSelect: 'none',
    });

    // --- Image ---
    const img = document.createElement('img');
    img.src = src;
    img.alt = alt || '';
    Object.assign(img.style, {
        maxWidth: '95vw',
        maxHeight: '95vh',
        width: 'auto',
        height: 'auto',
        objectFit: 'contain',
        borderRadius: '0',
        transition: 'transform 0.15s ease',
        transformOrigin: 'center center',
        cursor: 'grab',
        userSelect: 'none',
        WebkitUserDrag: 'none',
    });

    // --- Zoom indicator ---
    const zoomBadge = document.createElement('div');
    Object.assign(zoomBadge.style, {
        position: 'fixed',
        bottom: '24px',
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'rgba(0, 0, 0, 0.6)',
        border: '1px solid rgba(255,255,255,0.2)',
        color: '#fff',
        padding: '6px 16px',
        borderRadius: '20px',
        fontSize: '13px',
        fontWeight: '500',
        letterSpacing: '0.5px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        pointerEvents: 'none',
        transition: 'opacity 0.3s ease',
        opacity: '0',
        zIndex: '10001',
    });

    // --- Controls bar ---
    const controls = document.createElement('div');
    Object.assign(controls.style, {
        position: 'fixed',
        top: '16px',
        right: '16px',
        display: 'flex',
        gap: '8px',
        zIndex: '10001',
    });

    const makeBtn = (label, title) => {
        const btn = document.createElement('button');
        btn.textContent = label;
        btn.title = title;
        Object.assign(btn.style, {
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'rgba(0, 0, 0, 0.6)',
            color: '#fff',
            fontSize: '18px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.15s ease',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        });
        btn.addEventListener('mouseenter', () => {
            btn.style.background = 'rgba(0, 0, 0, 0.8)';
        });
        btn.addEventListener('mouseleave', () => {
            btn.style.background = 'rgba(0, 0, 0, 0.6)';
        });
        return btn;
    };

    const btnZoomIn = makeBtn('+', 'Zoom in');
    const btnZoomOut = makeBtn('−', 'Zoom out');
    const btnReset = makeBtn('⟲', 'Reset zoom');
    const btnClose = makeBtn('✕', 'Close (Esc)');

    controls.appendChild(btnZoomIn);
    controls.appendChild(btnZoomOut);
    controls.appendChild(btnReset);
    controls.appendChild(btnClose);

    overlay.appendChild(img);
    overlay.appendChild(zoomBadge);
    overlay.appendChild(controls);
    document.body.appendChild(overlay);

    // --- Helpers ---
    const applyTransform = (animate) => {
        img.style.transition = animate ? 'transform 0.2s ease' : 'none';
        img.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
        overlay.style.cursor = scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in';
        img.style.cursor = scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in';
    };

    let badgeTimeout;
    const showZoomBadge = () => {
        zoomBadge.textContent = `${Math.round(scale * 100)}%`;
        zoomBadge.style.opacity = '1';
        clearTimeout(badgeTimeout);
        badgeTimeout = setTimeout(() => {
            zoomBadge.style.opacity = '0';
        }, 1200);
    };

    const zoomTo = (newScale, animate = true) => {
        const clamped = Math.min(MAX_SCALE, Math.max(MIN_SCALE, newScale));
        // When zooming out past 1, reset position
        if (clamped <= 1) {
            translateX = 0;
            translateY = 0;
        } else {
            // Clamp pan so image doesn't fly off
            const maxPan = (clamped - 1) * 300;
            translateX = Math.max(-maxPan, Math.min(maxPan, translateX));
            translateY = Math.max(-maxPan, Math.min(maxPan, translateY));
        }
        scale = clamped;
        applyTransform(animate);
        showZoomBadge();
    };

    const resetZoom = () => {
        scale = 1;
        translateX = 0;
        translateY = 0;
        applyTransform(true);
        showZoomBadge();
    };

    // --- Cleanup ---
    const close = () => {
        document.removeEventListener('keydown', handleKeyDown, true);
        overlay.removeEventListener('wheel', handleWheel);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        isDragging = false;
        clearTimeout(badgeTimeout);
        overlay.remove();
    };

    // --- Event handlers ---
    const handleKeyDown = (e) => {
        if (e.metaKey || e.ctrlKey || e.altKey) return;
        const handledKeys = ['Escape', '+', '=', '-', '_', '0'];
        if (!handledKeys.includes(e.key)) return;
        e.stopImmediatePropagation();
        e.stopPropagation();
        e.preventDefault();
        if (e.key === 'Escape') close();
        if (e.key === '+' || e.key === '=') zoomTo(scale * (1 + ZOOM_STEP));
        if (e.key === '-' || e.key === '_') zoomTo(scale * (1 - ZOOM_STEP));
        if (e.key === '0') resetZoom();
    };

    const handleWheel = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
        zoomTo(scale * (1 + delta), false);
    };

    // Drag to pan
    const handleMouseDown = (e) => {
        if (scale <= 1) return;
        e.preventDefault();
        isDragging = true;
        dragStartX = e.clientX;
        dragStartY = e.clientY;
        lastTranslateX = translateX;
        lastTranslateY = translateY;
        applyTransform(false);
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        translateX = lastTranslateX + (e.clientX - dragStartX);
        translateY = lastTranslateY + (e.clientY - dragStartY);
        applyTransform(false);
    };

    const handleMouseUp = () => {
        if (!isDragging) return;
        isDragging = false;
        applyTransform(false);
    };

    // Double-click to toggle between 1x and 2x
    img.addEventListener('dblclick', (e) => {
        e.stopPropagation();
        if (scale > 1) {
            resetZoom();
        } else {
            zoomTo(2, true);
        }
    });

    // Click backdrop (not image) to close
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) close();
    });

    // Prevent img click from closing
    img.addEventListener('click', (e) => e.stopPropagation());

    // Bind events
    document.addEventListener('keydown', handleKeyDown, true);
    overlay.addEventListener('wheel', handleWheel, { passive: false });
    img.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // Button actions
    btnZoomIn.addEventListener('click', (e) => {
        e.stopPropagation();
        zoomTo(scale * (1 + ZOOM_STEP));
    });
    btnZoomOut.addEventListener('click', (e) => {
        e.stopPropagation();
        zoomTo(scale * (1 - ZOOM_STEP));
    });
    btnReset.addEventListener('click', (e) => {
        e.stopPropagation();
        resetZoom();
    });
    btnClose.addEventListener('click', (e) => {
        e.stopPropagation();
        close();
    });
}
